import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { SmartScan } from "@/components/SmartScan";

const TICKET_CATS = ["fuite", "ascenseur", "electricite", "propreté", "bruit", "autre"];

export default function ActionPage() {
  const { t } = useApp();
  const { isRole } = useAuth();
  const [mode, setMode] = useState(null);
  const canSmart = isRole("president", "tresorier");

  if (mode === "smart") return <SmartScan back={() => setMode(null)} />;
  if (mode === "ticket") return <ReportProblem onDone={() => setMode(null)} back={() => setMode(null)} />;

  return (
    <div className="space-y-4" data-testid="action-page">
      <h1 className="text-2xl font-bold">{t("action_title")}</h1>
      {canSmart && (
        <button onClick={() => setMode("smart")} data-testid="action-smart-scan"
          className="w-full rounded-2xl bg-gradient-to-br from-majorelle to-[#243b8f] text-white p-6 text-start flex items-center gap-4 active:scale-[0.98] transition-transform shadow-ios-lg">
          <Icons.Sparkles className="h-9 w-9" />
          <div><p className="font-bold text-lg">{t("ss_button")}</p><p className="text-white/70 text-sm">{t("ss_button_sub")}</p></div>
        </button>
      )}
      <button onClick={() => setMode("ticket")} data-testid="action-report"
        className="w-full rounded-2xl bg-terracotta text-white p-6 text-start flex items-center gap-4 active:scale-[0.98] transition-transform">
        <Icons.TriangleAlert className="h-9 w-9" />
        <div><p className="font-bold text-lg">{t("report_problem")}</p></div>
      </button>
    </div>
  );
}

function Back({ onClick, title }) {
  return (
    <button onClick={onClick} className="flex items-center gap-2 font-bold text-lg mb-2" data-testid="action-back">
      <Icons.ArrowLeft className="h-5 w-5 rtl:rotate-180" /> {title}
    </button>
  );
}

function ReportProblem({ onDone, back }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({ category: "fuite", description: "", location: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.description.trim()) return toast.error(t("description"));
    setBusy(true);
    try { await api.post("/tickets", { ...form, photos: [] }); toast.success(t("saved")); navigate("/tickets"); }
    catch { toast.error("Erreur"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="report-problem">
      <Back onClick={back} title={t("report_problem")} />
      <div>
        <label className="text-sm font-semibold text-muted-foreground">{t("ticket_category")}</label>
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="ticket-category"
          className="w-full h-12 rounded-xl border border-input bg-background px-3 mt-1 outline-none">
          {TICKET_CATS.map((c) => <option key={c} value={c}>{t("tcat_" + c)}</option>)}
        </select>
      </div>
      <div>
        <label className="text-sm font-semibold text-muted-foreground">{t("description")}</label>
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} data-testid="ticket-description"
          className="w-full rounded-xl border border-input bg-background px-3 py-2 mt-1 outline-none" />
      </div>
      <Field label={t("location")} value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="ticket-location" />
      <button onClick={save} disabled={busy} data-testid="ticket-save"
        className="w-full h-14 rounded-2xl bg-terracotta text-white font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-60">
        <Icons.Send className="h-5 w-5" /> {t("create")}
      </button>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-sm font-semibold text-muted-foreground">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full h-12 rounded-xl border border-input bg-background px-3 mt-1 outline-none" />
    </div>
  );
}
